import axios, { AxiosError, AxiosPromise } from 'axios';
import Http from 'shared/http';
import { ServerResponse } from 'data/types';
import { getViewLocationList } from 'data/location/location.api';
import { extractErrorMessage } from 'utils';
import { API_PATH_AFS, AccessFunction } from 'data/accessFunction/accessFunction.types';
import { Location } from 'data/location/location.types';
import { CollectionFunction } from 'data/collectionFunction/collectionFunction.types';
import { NodeData } from 'data/nodes/nodes.types';
import * as accessFunctionApi from 'data/accessFunction/accessFunction.api';
import * as collectionFunctionApi from 'data/collectionFunction/collectionFunction.api';
import * as xcipioNodesApi from 'data/nodes/nodes.api';

import { getConfiguredAccessFunctions } from 'data/accessFunction/accessFunction.api';
import {
  NetworkTopologyElementsEnum, NetworkTopologyFilter, API_PATH_GET_NETWORK_TOPOLOGY_FILTERS, NetworkTopologyFilterServerPayload, MOCK_DATA_ENUM, API_PATH_GET_NETWORK_TOPOLOGY_MOCKS, AdmfLeader, API_PATH_GET_NETWORK_TOPOLOGY_CONFIG
} from './network-topology.types';
import {
  generateAccessFunctions,
  generatePoiXcipioNodeMapMock,
  generateXcipioNodes,
  generateCollectionFunctions,
  generateCcpagMdf3ConnectionsMap,
  getAFMockLocations,
  getCcpagLocations,
  generateCcpagAccessFunctions,
} from './network-topology.mock';

const http = Http.getHttp();

async function buildNetworkTopologyRequest<T>(
  element: string,
  requestCallback: (...args: string[]) => AxiosPromise<ServerResponse<T>>,
  ...args: string[]
) {
  const response = await requestCallback(...args);
  return Promise.resolve({
    element,
    data: response.data.data
  });
}

function getSupportedFeaturesConfig(
  deliveryFunctionId: string,
): AxiosPromise<ServerResponse<any>> {
  return http.get(`/config/supported-features/${deliveryFunctionId}`);
}

function getNetworkTopologySupportedFeaturesConfig(
  deliveryFunctionId: string,
) {
  return buildNetworkTopologyRequest<AccessFunction>(
    NetworkTopologyElementsEnum.SupportedFeature,
    getSupportedFeaturesConfig,
    deliveryFunctionId
  );
}

function getNetworkTopologyConfiguredAfs(
  deliveryFunctionId: string,
) {
  return buildNetworkTopologyRequest<string[]>(
    NetworkTopologyElementsEnum.ConfiguredAfs,
    getConfiguredAccessFunctions,
    deliveryFunctionId
  );
}

function getCcpagDetails(deliveryFunctionId: string): AxiosPromise<ServerResponse<any>> {
  return http.get(`/ccpag-details/${deliveryFunctionId}`);
}

export function getADMFLeader(deliveryFunctionId: string): AxiosPromise<ServerResponse<any>> {
  return http.get(`/admf-leader/${deliveryFunctionId}`);
}

function getNetworkTopologyCcpagDetails(
  deliveryFunctionId: string,
) {
  return buildNetworkTopologyRequest<string[]>(
    NetworkTopologyElementsEnum.CcpagConnectionDetails,
    getCcpagDetails,
    deliveryFunctionId
  );
}

function getNetworkTopologyADMFLeader(deliveryFunctionId: string, mockData?: any) {

  function getRequestMock(): AxiosPromise<ServerResponse<any>> {
    return Promise.resolve({
      data: {
        data: mockData
      }
    } as any);
  }
  const getDataApiFn = mockData ? getRequestMock : getADMFLeader;

  return buildNetworkTopologyRequest<AdmfLeader>(
    'AdmfLeader',
    getDataApiFn,
    deliveryFunctionId
  );
}

function getNetworkTopologyAccessFunctions(
  deliveryFunctionId: string,
  mockData?: AccessFunction[]
) {
  function getRequestMock(): AxiosPromise<ServerResponse<any>> {
    return Promise.resolve({
      data: {
        data: mockData
      }
    } as any);
  }

  const getDataApiFn = mockData ? getRequestMock : accessFunctionApi.getAccessFunctionsWithViewOptions;

  return buildNetworkTopologyRequest<AccessFunction>(
    NetworkTopologyElementsEnum.AccessFunction,
    getDataApiFn,
    deliveryFunctionId
  );
}

function getNetworkTopologyViewLocationList(
  cities: string,
) {
  return buildNetworkTopologyRequest<Location>(
    NetworkTopologyElementsEnum.Location,
    getViewLocationList,
    cities
  );
}

function getCollectionFunctionList(
  deliveryFunctionId: string,
  mockData?: NodeData[]
) {

  function getRequestMock(): AxiosPromise<ServerResponse<any>> {
    return Promise.resolve({
      data: {
        data: mockData
      }
    } as any);
  }

  const getDataApiFn = mockData ? getRequestMock : collectionFunctionApi.getCollectionFunctionListWithViewOptions;

  return buildNetworkTopologyRequest<CollectionFunction>(
    NetworkTopologyElementsEnum.CollectionFunction,
    getDataApiFn,
    deliveryFunctionId
  );
}

function getXcipioNodes(
  deliveryFunctionId: string,
  mockData?: NodeData[]
) {

  function getRequestMock(): AxiosPromise<ServerResponse<any>> {
    return Promise.resolve({
      data: {
        data: mockData
      }
    } as any);
  }

  const getDataApiFn = mockData ? getRequestMock : xcipioNodesApi.getNodes;

  return buildNetworkTopologyRequest<NodeData>(
    NetworkTopologyElementsEnum.Xcipio,
    getDataApiFn,
    deliveryFunctionId
  );
}

function getPoiXcipioNodeMap(
  deliveryFunctionId: string,
  mockData?: any[]
) {
  function getPoiXcipioNodeMapRequest(): AxiosPromise<ServerResponse<any>> {
    return http.get('/poimdfmap');
  }

  function getRequestMock(): AxiosPromise<ServerResponse<any>> {
    return Promise.resolve({
      data: {
        data: mockData
      }
    } as any);
  }

  const getDataApiFn = mockData ? getRequestMock : getPoiXcipioNodeMapRequest;

  return buildNetworkTopologyRequest<NodeData>(
    NetworkTopologyElementsEnum.PoiXcipioNodeMap,
    getDataApiFn,
    deliveryFunctionId
  );
}

function getXcipioNodesMap(
  deliveryFunctionId: string,
  mockData?: { [key: string]: string }
) {
  function getPoiXcipioNodeMapRequest(): AxiosPromise<ServerResponse<any>> {
    return http.get('/poimdfmap'); // TODO: replace this endpoint with the real one after xrest side  is done
  }

  function getRequestMock(): AxiosPromise<ServerResponse<any>> {
    return Promise.resolve({
      data: {
        data: mockData
      }
    } as any);
  }

  const getDataApiFn = mockData ? getRequestMock : getPoiXcipioNodeMapRequest;

  return buildNetworkTopologyRequest<NodeData>(
    NetworkTopologyElementsEnum.CcpagMdf3Map,
    getDataApiFn,
    deliveryFunctionId
  );
}

// eslint-disable-next-line import/prefer-default-export
export async function getNetworkTopology(
  deliveryFunctionId: string,
  shouldUseNetworkTopologyMockData?: { [key: string ]: number },
  shouldUseNetworkTopologyDinamicLocations?: boolean,
) {

  if (shouldUseNetworkTopologyMockData) {
    const {
      afs, cfs, admf, mdf2, mdf3, ccpag
    } = shouldUseNetworkTopologyMockData;
    const totalPerAf = Math.floor(afs/6 + 1.5);
    const totalPercf = Math.floor(cfs/7 + 1.5);
    const locations = getAFMockLocations(shouldUseNetworkTopologyDinamicLocations);
    const afMocks = generateAccessFunctions(
      {
        NOK_5GUDM: totalPerAf,
        MVNR_5GSMSF: totalPerAf,
        NSN_SDM: totalPerAf,
        CASA_5GSMF: totalPerAf,
        NOK_5GSMF: totalPerAf,
        NOK_5GSMSF: totalPerAf,
      },
      locations,
    );

    const upfAfMocks = generateCcpagAccessFunctions(
      {
        ERK_UPF: ccpag,
      },
      getCcpagLocations(),
    );

    const smfAfMocks = generateCcpagAccessFunctions(
      {
        ERK_SMF: ccpag,
      },
      getCcpagLocations(),
      true
    );

    const xcipioNodesMocks = generateXcipioNodes({
      amdf: {
        total: admf,
        locations: [
          { country: 'US', stateCode: 'New Jersey', city: 'Trenton' },
          { country: 'US', stateCode: 'New Jersey', city: 'Trenton' },
        ]
      },
      mdf2: {
        total: mdf2,
        locations: [
          { country: 'US', stateCode: 'New Jersey', city: 'Trenton' },
          { country: 'US', stateCode: 'New Jersey', city: 'Trenton' },
          { country: 'US', stateCode: 'New Jersey', city: 'Trenton' },
          { country: 'US', stateCode: 'New Jersey', city: 'Trenton' },
         ]
      },
      mdf3: {
        total: mdf3,
        locations: [
          { country: 'US', stateCode: 'New Jersey', city: 'Trenton' },
          { country: 'US', stateCode: 'New Jersey', city: 'Trenton' },
          { country: 'US', stateCode: 'New Jersey', city: 'Trenton' },
          { country: 'US', stateCode: 'New Jersey', city: 'Trenton' },
        ]
      },
      ccpag: {
        total: ccpag,
        locations: getCcpagLocations(),
      },

    });
    const cfMocks = generateCollectionFunctions(
      {
        GPRS: totalPercf,
        ETSIIP: totalPercf,
        TIA1066: totalPercf,
        'T1.678': totalPercf,
        CALEA: totalPercf,
        TIA1072: totalPercf,
        T1IAS: totalPercf,
      },
      locations,
    );

    const cities = await getNetworkTopologyViewLocationList('cities');
    const poiXcipioNodeMapMock = generatePoiXcipioNodeMapMock([...upfAfMocks, ...smfAfMocks, ...afMocks], cfMocks, xcipioNodesMocks, cities.data)
    const ccpagMdf3ConnectionsMap = generateCcpagMdf3ConnectionsMap(xcipioNodesMocks, cities.data || []);
    try {
      return await Promise.all([
        getNetworkTopologyAccessFunctions(deliveryFunctionId, [...upfAfMocks, ...smfAfMocks, ...afMocks]),
        getNetworkTopologyViewLocationList('cities'),
        getCollectionFunctionList(deliveryFunctionId, cfMocks),
        getXcipioNodes(deliveryFunctionId, xcipioNodesMocks),
        getNetworkTopologySupportedFeaturesConfig(deliveryFunctionId),
        getPoiXcipioNodeMap(deliveryFunctionId, poiXcipioNodeMapMock),
        getXcipioNodesMap(deliveryFunctionId, ccpagMdf3ConnectionsMap)
      ]);
    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      throw new Error(errorMessage);
    }
  }

   try {
    return await Promise.all([
      getNetworkTopologyAccessFunctions(deliveryFunctionId),
      getNetworkTopologyViewLocationList('cities'),
      getCollectionFunctionList(deliveryFunctionId),
      getXcipioNodes(deliveryFunctionId),
      getNetworkTopologySupportedFeaturesConfig(deliveryFunctionId),
      getPoiXcipioNodeMap(deliveryFunctionId),
      getNetworkTopologyConfiguredAfs(deliveryFunctionId),
      getNetworkTopologyCcpagDetails(deliveryFunctionId),
      getNetworkTopologyADMFLeader(deliveryFunctionId)
    ])
  } catch (e) {
    const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
    throw new Error(errorMessage);
  }
}

export function getNetworkTopologyCustomFilters(): AxiosPromise<ServerResponse<NetworkTopologyFilterServerPayload[]>> {
  return http.get(`${API_PATH_GET_NETWORK_TOPOLOGY_FILTERS}`);
}

export function createNetworkTopologyCustomFilter(body: NetworkTopologyFilter): AxiosPromise<ServerResponse<void>> {
  return http.post(`${API_PATH_GET_NETWORK_TOPOLOGY_FILTERS}`, { id: body.id ? body.id : '', filterJson: JSON.stringify(body) });
}

export function deleteNetworkTopologyCustomFilter(id: string): AxiosPromise<ServerResponse<void>> {
  return http.delete(`${API_PATH_GET_NETWORK_TOPOLOGY_FILTERS}/${id}`);
}

export function getMockDataFile(filename: MOCK_DATA_ENUM): AxiosPromise<ServerResponse<any>> {
  const fileNameString = MOCK_DATA_ENUM[filename];
  return http.get(`${API_PATH_GET_NETWORK_TOPOLOGY_MOCKS}/${fileNameString}`);
}
export function getNetworkTopologyConfig(): AxiosPromise<ServerResponse<string[]>> {
  return http.get(`${API_PATH_GET_NETWORK_TOPOLOGY_CONFIG}`)
}
